<script>
import isFunction from 'lodash/isFunction';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CarModel from '@models/Car';
import CarTypeModel from '@models/CarType';
import CarOwnerModel from '@models/CarOwner';
import BlockingContainer from '@components/BlockingContainer';
import FileWrapper from '@components/FileWrapper';
import FileDownloadLink from '@components/FileDownloadLink';
import editFormChange from '@mixins/editFormChange';
import EntityMultiselect from '@components/EntityMultiselect';

export default {
  components: {
    BlockingContainer,
    FontAwesomeIcon,
    FileWrapper,
    FileDownloadLink,
    EntityMultiselect,
  },
  mixins: [editFormChange],
  props: {
    isCarUseForm: {
      type: Boolean,
      default: false,
    },
    // eslint-disable-next-line
    value: {
      default: null,
    },
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
  },
  data() {
    return {
      car: {
        license_plate: null,
        car_type_id: null,
        car_type: null,
        car_owner_id: null,
        car_owner: null,
        verified: 0,
        sva_print: 0,
        zub: [],
      },
      defaultCar: {
        license_plate: null,
        car_type_id: null,
        car_type: null,
        car_owner_id: null,
        car_owner: null,
        verified: 0,
        sva_print: 0,
        zub: [],
      },
      carTypes: [],
      carOwnerModel: CarOwnerModel,
    };
  },
  watch: {
    value(carUse) {
      if (carUse.car) {
        this.car = carUse.car;
      }
    },
  },
  mounted() {
    CarTypeModel.select(['id', 'name'])
      .orderBy(`name`)
      .page(1)
      .limit(100)
      .get()
      .then((response) => {
        this.carTypes = response.data;
        this.$wait.end('car types loading');
      })
      .catch(() => {
        this.$wait.end('car types loading');
      });
    if (this.loadedId) {
      this.$wait.start('car loading');
      CarModel.find(this.loadedId).then((response) => {
        this.car = Object.assign({}, this.car, response.car);
        this.$wait.end('car loading');
        this.$nextTick(() => {
          this.initChangeWatcher(['car'], this.validate);
        });
      });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('car loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      if (this.loadedId) {
        this.car.id = this.loadedId;
      }
      new CarModel(
        Object.assign({}, this.car, {
          zub: this.car.zub && Array.isArray(this.car.zub) ? this.car.zub[0] : this.car.zub,
        })
      )
        .save()
        .then((response) => {
          this.car = response.car;
          this.$emit('submitted', callback && isFunction(callback));
          this.$emit('new-car', this.car);
          // clear form
          this.car = Object.assign({}, this.defaultCar);
          this.$validator.reset();
          if (callback && isFunction(callback)) callback();
          this.$wait.end('car loading');
        })
        .catch(() => this.$wait.end('car loading'));
    },
    removeZUB() {
      if(this.car.verified === 1) {
         this.$toasted.error(this.$t('errors.cannot_remove_zub_from_verified_car'));
      } else {
         this.car.zub = [];
      }
    }
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('car loading')"></BlockingContainer>
    <b-form @submit.prevent="validate">
      <b-form-row>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.license_plate_format') }}:
              <span class="status-star text-danger">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="carLicensePlatePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="carLicensePlatePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.license_plate') }}
                </b-popover>
              </span>
            </template>
            <b-form-input
              v-model="car.license_plate"
              v-validate="'required|max:255'"
              data-vv-value-path="localValue"
              data-vv-name="license_plate"
              :data-vv-as="$t('fields.license_plate')"
              type="text"
              :state="errors.has('license_plate') ? false : null"
              aria-describedby="input-feedback-license_plate"
              :class="errors.has('license_plate', 'car-form') ? 'is-invalid' : null"
              :placeholder="$t('placeholders.enter_license_plate')"
              class="text-uppercase"
            >
            </b-form-input>
            <b-form-invalid-feedback id="input-feedback-license_plate">
              <template v-show="errors.has('license_plate')">{{ errors.first('license_plate') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.car_type') }}:
              <span class="status-star text-danger">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="carTypePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="carTypePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.car_type') }}
                </b-popover>
              </span>
            </template>
            <b-form-select
              v-model="car.car_type_id"
              v-validate="'required'"
              data-vv-value-path="localValue"
              data-vv-name="car_type_id"
              :disabled="isCarUseForm && car.verified === 1"
              :data-vv-as="$t('fields.car_type')"
              :state="errors.has('car_type_id', 'car-form') ? false : null"
              aria-describedby="input-feedback-car_type_id"
            >
              <option :value="null">{{ $t('placeholders.car_type') }}</option>
              <option v-for="carType in carTypes" :key="`carTypeSelect${carType.id}`" :value="carType.id">
                {{ carType.name }}
              </option>
            </b-form-select>
            <b-form-invalid-feedback id="input-feedback-car_id">
              <template v-show="errors.has('car_id', 'car-form')">{{ errors.first('car_id', 'car-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.owner') }}:
          <span class="status-star text-danger">*</span>
          <span class="near-label-info text-secondary" @click.stop>
            <FontAwesomeIcon id="carOwnerPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
            <b-popover target="carOwnerPopover" triggers="focus" placement="rightbottom">
              {{ $t('infos.car_owner') }}
            </b-popover>
          </span>
        </template>
        <EntityMultiselect
          v-model="car.car_owner_id"
          v-validate="'required'"
          data-vv-name="car_owner_id"
          :disabled="isCarUseForm && car.verified === 1"
          :data-vv-as="$t('fields.owner')"
          :initial-value="car.car_owner"
          :state="errors.has('car_owner_id', 'car-form')"
          :placeholder="$t('placeholders.owner')"
          :model="carOwnerModel"
        >
        </EntityMultiselect>
        <div v-show="errors.has('car_owner_id', 'car-form')" class="invalid-feedback d-block m-0">
          {{ errors.first('car_owner_id', 'car-form') }}
        </div>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.zub') }}:
          <span class="status-star text-success">*</span>
          <span class="near-label-info text-secondary" @click.stop>
            <FontAwesomeIcon id="zubPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
            <b-popover target="zubPopover" triggers="focus" placement="rightbottom">
              {{ $t('infos.zub') }}
            </b-popover>
          </span>
        </template>
        <FileDownloadLink v-if="car.zub && !Array.isArray(car.zub)" :file="car.zub" :disabled="isCarUseForm && car.verified === 1" @remove="removeZUB" />
        <FileWrapper
          v-else
          v-model="car.zub"
          v-validate="{ image: true }"
          :disabled="isCarUseForm && car.verified === 1"
          :data-vv-as="$t('fields.zub')"
          :state="errors.has('zub', 'car-form') ? false : null"
          :placeholder="$t('placeholders.zub')"
          accept="image/*"
          aria-describedby="input-feedback-zub"
          name="zub"
        >
        </FileWrapper>

        <div v-show="errors.has('zub', 'car-form')" class="invalid-feedback d-block mt-1">
          {{ errors.first('zub', 'car-form') }}
        </div>
      </b-form-group>
      <b-button type="submit" :disabled="errors.any()" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')">
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  </div>
</template>